import { usePublicProjectReader, publicQueryKey, projectReaderGet } from '@/lib/public-project-reader';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost, apiPatch, apiDelete } from '@/lib/api';
import type { Comment, CreateCommentInput, UpdateCommentInput } from '@/lib/schemas';

// ============================================
// QUERY KEYS
// ============================================

export const commentKeys = {
    all: ['comments'] as const,
    lists: () => [...commentKeys.all, 'list'] as const,
    list: (taskId: string) => [...commentKeys.lists(), taskId] as const,
    detail: (id: string) => [...commentKeys.all, 'detail', id] as const,
};

// ============================================
// TYPES
// ============================================

export interface CommentsResponse {
    comments: Comment[];
    total: number;
}

// ============================================
// HOOKS
// ============================================

export function useComments(taskId: string) {
    const reader = usePublicProjectReader();
    return useQuery({
        queryKey: publicQueryKey(reader, commentKeys.list(taskId)),
        queryFn: async () => {
            const data = await projectReaderGet<CommentsResponse>(reader, `/tasks/${taskId}/comments`);
            return data;
        },
        refetchInterval: reader ? 10000 : false,
        enabled: !!taskId,
        staleTime: 30 * 1000, // 30 seconds
    });
}

export function useCreateComment(taskId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: CreateCommentInput) => {
            return apiPost<Comment>(`/tasks/${taskId}/comments`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: commentKeys.list(taskId) });
        },
    });
}

export function useUpdateComment(commentId: string, taskId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: UpdateCommentInput) => {
            return apiPatch<Comment>(`/comments/${commentId}`, data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: commentKeys.list(taskId) });
        },
    });
}

export function useDeleteComment(taskId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (commentId: string) => {
            return apiDelete<{ message: string }>(`/comments/${commentId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: commentKeys.list(taskId) });
        },
    });
}
